import { useId } from "react";
import { Switch } from "components/fields";

/**
 * SettingToggleRow — label and description on the left, Switch on the right.
 * Toggles save immediately (autosave); pair a list of rows with
 * `<AutosaveHint />` so users know there is no Save button.
 *
 * @param {object} props
 * @param {React.ReactNode} props.label Row label; it is the switch's accessible name.
 * @param {React.ReactNode} [props.description] Muted helper text (aria-describedby).
 * @param {boolean} props.checked
 * @param {(checked: boolean) => void} props.onChange Receives the next value.
 * @param {boolean} [props.disabled=false]
 * @param {string} [props.id]
 */
export default function SettingToggleRow({ label, description, checked, onChange, disabled = false, id }) {
  const autoId = useId();
  const switchId = id || autoId;
  const labelId = `${switchId}-label`;
  const descriptionId = description ? `${switchId}-description` : undefined;

  return (
    <div className="flex items-center justify-between gap-4 border-b border-line py-4 first:pt-0 last:border-0 last:pb-0">
      <div className="min-w-0">
        <label id={labelId} htmlFor={switchId} className="text-sm font-semibold text-fg">
          {label}
        </label>
        {description && (
          <p id={descriptionId} className="mt-0.5 text-sm text-fg-muted">
            {description}
          </p>
        )}
      </div>
      <Switch
        id={switchId}
        checked={checked}
        disabled={disabled}
        aria-labelledby={labelId}
        aria-describedby={descriptionId}
        onChange={(e) => onChange(e.target.checked)}
      />
    </div>
  );
}

export function AutosaveHint() {
  return <p className="mt-4 text-xs text-fg-muted">Changes are saved automatically.</p>;
}
